import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useT } from '../i18n'

const G = '#C89B4C'
const TX3 = '#8A8480'
const TX = '#F4F1EA'
const BD = 'rgba(244,238,225,0.14)'

const Icons = {
  home:  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z"/><polyline points="9 21 9 12 15 12 15 21"/></svg>,
  list:  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z"/><polyline points="14 3 14 9 20 9"/></svg>,
  grid:  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  user:  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21v-1a6 6 0 016-6h4a6 6 0 016 6v1"/></svg>,
  plus:  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  dash:  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/><rect x="14" y="12" width="7" height="9" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/></svg>,
  coin:  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="6" rx="8" ry="3"/><path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6"/><path d="M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6"/></svg>,
  pkg:   <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
  more:  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
}

type NavItem = { to: string; icon: JSX.Element; label: string; end?: boolean }

export default function BottomNav() {
  const { user, isAdmin } = useAuth()
  const lang = user?.language ?? 'ru'
  const tr = useT(lang)
  const navigate = useNavigate()

  const adminItems: NavItem[] = [
    { to: '/admin', end: true, icon: Icons.dash, label: tr('overview') },
    { to: '/admin/orders', icon: Icons.list, label: tr('orders') },
    { to: '/admin/payments', icon: Icons.coin, label: tr('payments') },
    { to: '/admin/products', icon: Icons.pkg, label: tr('products') },
    { to: '/admin/more', icon: Icons.more, label: tr('more') },
  ]

  const clientItems: NavItem[] = [
    { to: '/home',    icon: Icons.home, label: tr('home') },
    { to: '/orders',  icon: Icons.list, label: tr('orders') },
    { to: '/catalog', icon: Icons.grid, label: tr('catalog') },
    { to: '/profile', icon: Icons.user, label: tr('profile') },
  ]

  const navStyle: React.CSSProperties = {
    position: 'fixed', left: 0, right: 0, bottom: 0,
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
    background: 'rgba(10,16,30,0.90)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderTop: `1px solid ${BD}`,
    display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end',
    zIndex: 50, height: 72,
  }

  const itemStyle = (isActive: boolean): React.CSSProperties => ({
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 4, padding: '12px 14px 10px',
    color: isActive ? G : TX3,
    textDecoration: 'none',
    transition: 'color 0.15s',
  })

  const labelStyle: React.CSSProperties = {
    fontSize: 9, fontWeight: 500,
    letterSpacing: '0.06em', textTransform: 'uppercase',
    fontFamily: "'Inter', sans-serif",
  }

  if (isAdmin) {
    return (
      <nav className="mobile-only" style={navStyle}>
        {adminItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            style={({ isActive }) => itemStyle(isActive)}
          >
            {item.icon}
            <span style={labelStyle}>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    )
  }

  // Client nav: Home | Orders | FAB | Catalog | Profile
  const [left1, left2] = clientItems.slice(0, 2)
  const [right1, right2] = clientItems.slice(2)

  return (
    <nav className="mobile-only" style={navStyle}>
      <NavLink to={left1.to} end={left1.end} style={({ isActive }) => itemStyle(isActive)}>
        {left1.icon}
        <span style={labelStyle}>{left1.label}</span>
      </NavLink>

      <NavLink to={left2.to} end={left2.end} style={({ isActive }) => itemStyle(isActive)}>
        {left2.icon}
        <span style={labelStyle}>{left2.label}</span>
      </NavLink>

      {/* Gold FAB — center */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 6 }}>
        <button
          onClick={() => navigate('/custom-order')}
          style={{
            width: 52, height: 52, borderRadius: '50%',
            background: `linear-gradient(140deg, #C89B4C 0%, #9C7A33 100%)`,
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginTop: -20,
            boxShadow: '0 4px 20px rgba(200,155,76,0.40)',
            color: '#0D1526',
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.94)')}
          onMouseUp={e => (e.currentTarget.style.transform = 'scale(1)')}
          onTouchStart={e => (e.currentTarget.style.transform = 'scale(0.94)')}
          onTouchEnd={e => (e.currentTarget.style.transform = 'scale(1)')}
        >
          {Icons.plus}
        </button>
        <span style={{ ...labelStyle, color: TX3, marginTop: 4, fontSize: 8 }}>
          {lang === 'uz' ? 'Ariza' : 'Заявка'}
        </span>
      </div>

      <NavLink to={right1.to} end={right1.end} style={({ isActive }) => itemStyle(isActive)}>
        {right1.icon}
        <span style={labelStyle}>{right1.label}</span>
      </NavLink>

      <NavLink to={right2.to} end={right2.end} style={({ isActive }) => itemStyle(isActive)}>
        {right2.icon}
        <span style={labelStyle}>{right2.label}</span>
      </NavLink>
    </nav>
  )
}
